import { useEffect, useState } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Mail, Loader2, CheckCircle2, X } from "lucide-react";

/**
 * Landing-page top banner announcing GLPFlo's current shipping
 * footprint (Florida) and inviting visitors from other states to
 * request coverage. Submissions go to POST /api/pharmacies/shipping/waitlist
 * where they upsert into `state_waitlist` and surface on the admin
 * `/admin/shipping-waitlist` dashboard. Admins can trigger a
 * coverage-live email blast per-state via
 * POST /api/admin/shipping-waitlist/notify-state/{ST}.
 *
 * Anonymous-friendly — no auth required. The X button hides it for
 * the session (sessionStorage) so it never blocks the hero on repeat
 * scrolls but returns on the next visit.
 */

const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"],
  ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"],
  ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"],
  ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"],
  ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"],
  ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"], ["NC", "North Carolina"],
  ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"],
  ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"],
  ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"],
  ["WY", "Wyoming"], ["DC", "District of Columbia"],
];

const DISMISS_KEY = "glpflo:state-banner-dismissed";

export default function LandingStateRequestBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [enabled, setEnabled] = useState(true);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  // Admin flag — banner defaults to on; a false response hides it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/landing/flags");
        if (!cancelled) setEnabled(data?.state_banner !== false);
      } catch { /* fallback: keep it enabled so a bad admin API can't hide the banner permanently */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-detect the visitor's state via the existing IP → state lookup
  // so most requesters only need to type an email. If the visitor is
  // already in Florida (our current coverage) we leave the picker blank
  // — the banner is irrelevant to them but they can still opt in for
  // future notifications. Silent on error.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/geo/state");
        const code = String(data?.state || "").toUpperCase();
        if (cancelled || !code || code === "FL") return;
        // Only pre-select if the code is one of the choices in the picker.
        if (US_STATES.some(([c]) => c === code)) setState((s) => s || code);
      } catch { /* silent — picker stays blank */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !enabled) return null;

  function dismiss() {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* private mode / SSR */ }
  }

  async function submit(e) {
    e.preventDefault();
    const v = (email || "").trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error("Enter a valid email"); return; }
    if (!state) { toast.error("Pick your state"); return; }
    setSubmitting(true);
    try {
      await API.post("/pharmacies/shipping/waitlist", { email: v, state, source: "landing-top-banner" });
      setJoined(true);
      toast.success(`We'll email you when ${state} comes online.`);
    } catch {
      toast.error("Couldn't add you — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="relative w-full bg-gradient-to-r from-purple-700 via-purple-600 to-fuchsia-600 text-white"
      data-testid="landing-state-request-banner"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-1.5 sm:py-4 flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3 xl:flex-nowrap">
        <div className="flex items-start lg:items-center gap-2 sm:gap-2.5 flex-1 xl:flex-none min-w-0 xl:min-w-0">
          <div className="hidden sm:flex w-8 h-8 rounded-lg bg-white/15 items-center justify-center shrink-0">
            <MapPin className="w-4 h-4" />
          </div>
          <p className="min-w-0 xl:min-w-max text-[12px] sm:text-[15px] xl:text-[13px] 2xl:text-[15px] leading-snug font-semibold xl:whitespace-nowrap">
            <span className="font-bold">🌴 Now serving Florida</span>
            <span className="text-white/80 font-normal"> · Starting dose from </span>
            <span
              className="font-extrabold"
              style={{
                textShadow:
                  "0 0 6px rgba(255,255,255,0.85), 0 0 14px rgba(240,171,252,0.9), 0 0 24px rgba(217,70,239,0.7)",
                animation: "pulse-glow 2.4s ease-in-out infinite",
              }}
            >
              $79.99
            </span>
            <span className="text-white/80 font-normal"> · East Coast next — </span>
            <span className="font-semibold">join the waitlist for your state:</span>
          </p>
        </div>

        {joined ? (
          <div
            className="inline-flex items-center gap-2 bg-emerald-500/95 rounded-full px-3 py-1.5 font-semibold text-xs sm:text-sm shrink-0"
            data-testid="landing-state-banner-joined"
          >
            <CheckCircle2 className="w-4 h-4" />
            You&apos;re on the list for {state} — we&apos;ll email you.
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="flex flex-row items-stretch gap-1.5 sm:gap-2 shrink-0 w-full sm:w-auto"
            data-testid="landing-state-banner-form"
          >
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="rounded-md sm:rounded-lg bg-white/95 text-slate-900 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 w-20 sm:min-w-[9rem] sm:w-auto focus:outline-none focus:ring-2 focus:ring-white"
              data-testid="landing-state-banner-state"
              aria-label="Your state"
            >
              <option value="">State</option>
              {US_STATES.map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
            <div className="flex items-center gap-1 sm:gap-1.5 bg-white/95 rounded-md sm:rounded-lg px-2 sm:px-2.5 flex-1 min-w-0 sm:min-w-[15rem]">
              <Mail className="hidden sm:block w-4 h-4 text-slate-400 shrink-0" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 text-xs sm:text-sm text-slate-900 bg-transparent py-1.5 sm:py-2 focus:outline-none min-w-0"
                data-testid="landing-state-banner-email"
                aria-label="Email address"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md sm:rounded-lg bg-white text-purple-700 hover:bg-purple-50 font-bold text-xs sm:text-sm px-2.5 sm:px-4 py-1.5 sm:py-2 disabled:opacity-70 flex items-center justify-center gap-1.5 shrink-0"
              data-testid="landing-state-banner-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<span><span className="hidden sm:inline">Join waitlist</span><span className="sm:hidden">→</span></span>)}
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={dismiss}
          className="absolute top-1.5 right-1.5 sm:static sm:ml-1 shrink-0 w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center"
          aria-label="Dismiss"
          data-testid="landing-state-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
